// constants
import { DEFAULT_JUSPAY_MERCHANTID } from "@payment-client/constants/Juspay";

// internal deps
import {
    getBridgeSdk,
    NEWRELIC_TABLES,
    ProcessTransactionActionPayload,
    TransactionReqData,
    recordNREvent,
} from "@swiggy-private/checkout-core";
import { JuspayCardResponsePayload } from "@swiggy-private/native-bridge";
import {
    CardDetails,
    ConfirmPennyTransactionResponse,
    CreatePennyTransactionResponse,
    ProceedNewCardDetails,
    VALID_PAYMENT_METHODS,
} from "@swiggy-private/payment-types";
import { isEmpty, Storage } from "@swiggy-private/core-ui";

// helpers
import { getCardTokenizeValue, processTxnUsingJusPayRedirectFlow, shouldUseJuspayRedirectFlow } from "./JuspayHelpers";
import { ProcessOrderReturnType } from "./OrderHelper";
import { getCardBinFromCardNumber, getCardBinFromExtraParams } from "./CardHelper";

// interfaces
import { ApiResponse } from "@payment-client/interfaces/Api";
import { SecureCardDataProps } from "@payment-client/components/payment-modes/components/secure-card";

// constants
import { API_SUCCESS_STATUS_CODE } from "@payment-client/constants/StatusCodes";
import { PAYMENT_MODES_STORAGE_KEYS } from "@payment-client/constants/PaymentModes";

interface JuspayCardPaymentParams {
    method: CardDetails;
    juspayMeta: CreatePennyTransactionResponse["juspayMeta"];
    orderAmount: string;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    extraParams: any;
    cardBinNumber: string;
    merchantId?: string;
    tokenize?: boolean | null;
    nickname?: string;
}

export const getCardBinNumberFromTxnData = (transactionData: TransactionReqData): string => {
    const extraParams = transactionData?.extraParams ?? {};

    return getCardBinFromExtraParams(extraParams) ?? transactionData.cardDetails.cardIsin ?? "";
};

export const getCreateTransactionParams = ({
    transactionData,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    isJuspaySdkAvailable,
}: {
    transactionData: TransactionReqData;
    isJuspaySdkAvailable: boolean;
}): {
    transactionType: string;
    paymentMethod: string;
    cardBinNumber: string;
    amount: number;
    isJuspayNative: boolean;
    paymentReturnUrl?: string;
} => {
    return {
        transactionType: "PENNY_DROP",
        paymentMethod: "Juspay",
        cardBinNumber: getCardBinNumberFromTxnData(transactionData),
        amount: 2,
        // TODO: enable once jdk flow is working
        isJuspayNative: isJuspaySdkAvailable,
    };
};

export const getPartnerOrderIdFromPennyTxnResponse = (
    data: CreatePennyTransactionResponse | ConfirmPennyTransactionResponse,
): string => {
    if (data.paymentPartnerOrderId != null) {
        return String(data.paymentPartnerOrderId);
    }
    return String(data.paymentTxnId);
};

function payUsingJuspaySavedCard(
    orderId: string,
    params: JuspayCardPaymentParams,
): Promise<JuspayCardResponsePayload | undefined> {
    return getBridgeSdk().payUsingJuspaySavedCard({
        callbackUrl: window.location.href,
        juspayParams: {
            orderId,
            orderAmount: params.orderAmount,
            merchantId: params.merchantId || DEFAULT_JUSPAY_MERCHANTID,
            redirectAfterPayment: true,
            paymentMethodType: "CARD",
            paymentMethod: params.method.cardBrand ?? "",
            returnUrl: `${window.location.href}.*`,
            cardSecurityCode: !isEmpty(params.extraParams.cvv) ? params.extraParams.cvv : null,
            cardToken: params.method.cardToken,
            cardBin: params.cardBinNumber,
            nickname: params.nickname || "",
            tokenize: params.tokenize,
            saveToLocker: true,
            ...params.juspayMeta,
        },
    });
}

function payUsingJuspayNewCard(
    orderId: string,
    params: JuspayCardPaymentParams,
): Promise<JuspayCardResponsePayload | undefined> {
    return getBridgeSdk().payUsingJuspayNewCard({
        callbackUrl: window.location.href,
        juspayParams: {
            orderId,
            orderAmount: params.orderAmount,
            merchantId: params.merchantId || DEFAULT_JUSPAY_MERCHANTID,
            paymentMethodType: "CARD",
            returnUrl: `${window.location.href}.*`,
            cardSecurityCode: !isEmpty(params.extraParams.cvv) ? params.extraParams.cvv : null,
            saveToLocker: params.extraParams.saveCard,
            cardNumber: params.extraParams.cardNumber,
            cardExpMonth: params.extraParams.month,
            nameOnCard: params.extraParams.name,
            cardExpYear: params.extraParams.year,
            cardBin: params.cardBinNumber,
            nickname: params.nickname || "",
            tokenize: params.tokenize,
            ...params.juspayMeta,
        },
    });
}

export async function processTransaction(
    processTransactionData: ProcessTransactionActionPayload,
): Promise<void | ProcessOrderReturnType> {
    const {
        transaction,
        merchantInfo,
        transactionReqData: { cardDetails, extraParams, paymentMethod, isNonSecureCard },
        useJuspaySDK,
    } = processTransactionData;

    const orderId = getPartnerOrderIdFromPennyTxnResponse(transaction);
    const methodName = String(transaction.paymentMethod);

    recordNREvent({
        eventType: NEWRELIC_TABLES.IMTR_DEBUG_LOGS,
        type: "TRANSACTION_HELPER",
        params: JSON.stringify({
            meta: merchantInfo?.merchantId,
            method: methodName,
        }),
        juspayMeta: JSON.stringify(transaction.juspayMeta),
    });

    // destructure juspayinfo for direct integration
    const { tokenRequester, tokenInfo } = transaction;
    const directIntegrationParams = {
        tokenRequester,
        juspayMetaData: {
            temporaryToken: tokenInfo?.juspayInfo?.temporaryToken,
            providerCategory: tokenInfo?.juspayInfo?.providerCategory,
            requesterId: tokenInfo?.juspayInfo?.requesterId,
            tokenType: tokenInfo?.juspayInfo?.tokenType,
        },
    };

    const authTypeForCard: string = transaction.juspayMeta?.authTypeForCard;
    try {
        if (methodName.toUpperCase() === "JUSPAY") {
            const isNewCard = paymentMethod === VALID_PAYMENT_METHODS.NEW_CARD;
            if (!useJuspaySDK || shouldUseJuspayRedirectFlow()) {
                return processTxnUsingJusPayRedirectFlow(
                    processTransactionData,
                    isNewCard ? "NEW_CARD" : "CARD",
                    isNonSecureCard ?? false,
                );
            }

            const cardBinNumber = transaction.binNumber;

            const params: JuspayCardPaymentParams = {
                extraParams,
                cardBinNumber,
                orderAmount: String(2),
                method: cardDetails,
                juspayMeta: { ...transaction.juspayMeta, authTypeForCard, ...directIntegrationParams },
                tokenize: isNewCard ? getCardTokenizeValue(cardDetails, extraParams) : true,
                merchantId: merchantInfo?.merchantId || DEFAULT_JUSPAY_MERCHANTID,
            };

            if (extraParams && extraParams.nickName) {
                params.nickname = extraParams.nickName;
            }

            await (isNewCard ? payUsingJuspayNewCard(orderId, params) : payUsingJuspaySavedCard(orderId, params));

            return { orderId };
        }
        return { orderId };
    } catch (err) {
        return { orderId };
    }
}

export function handleConfirmTransactionSuccess(
    response: ApiResponse<ConfirmPennyTransactionResponse>,
): ConfirmPennyTransactionResponse {
    const { statusCode, data } = response;
    if (statusCode !== API_SUCCESS_STATUS_CODE || data == null || data.paymentTxnStatus === "failed") {
        throw response;
    }

    return data;
}

export const prepareCardDetailsFromData = (details: ProceedNewCardDetails): CardDetails => {
    return {
        cardBrand: details.brand,
        cardExpMonth: details.month,
        cardExpYear: details.year,
        cardFingerprint: "",
        cardIsin: getCardBinFromCardNumber(String(details.cardNumber)),
        cardIssuer: "",
        cardNumber: details.cardNumber,
        cardReference: "",
        cardToken: "",
        cardType: "",
        fullImageUrl: "",
        iconUrl: details.cardIcon?.id || "",
        isFoodCard: false,
        isSecured: false,
        nameOnCard: details.name,
        nickname: details.nickName,
        tokenizeSupport: details.tokenizeSupport || false,
    };
};

export const isProceedNewCardDetails = (
    details: CardDetails | ProceedNewCardDetails,
): details is ProceedNewCardDetails => {
    return !(details as CardDetails).cardFingerprint;
};

export const getOnProcessParams = (
    details: CardDetails | ProceedNewCardDetails,
    extraParams: object,
    nickName?: string,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
): { cardDetails: CardDetails; additionalParams: { [key: string]: any } } => {
    let cardDetails;
    let additionalParams: {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        [key: string]: any;
    };
    if (isProceedNewCardDetails(details)) {
        cardDetails = prepareCardDetailsFromData(details);
        additionalParams = {
            ...details,
            ...extraParams,
        };
    } else {
        cardDetails = details;
        additionalParams = extraParams;
        if (nickName) {
            additionalParams.nickName = nickName;
        }
    }

    return {
        additionalParams,
        cardDetails,
    };
};

export const getAddCardDetails = (): ProceedNewCardDetails | undefined => {
    return Storage.getItem<ProceedNewCardDetails>(PAYMENT_MODES_STORAGE_KEYS.ADD_NEW_CARD);
};

export const getManageCardDetails = (): CardDetails | undefined => {
    return Storage.getItem<CardDetails>(PAYMENT_MODES_STORAGE_KEYS.MANAGE_CARD);
};

export const getSecureCardData = (transactionReqData: TransactionReqData): SecureCardDataProps => {
    let secureCardData: SecureCardDataProps = {};

    if (transactionReqData.paymentMethod === VALID_PAYMENT_METHODS.NEW_CARD) {
        secureCardData = {
            newCardDetails: getAddCardDetails(),
            paymentMethod: VALID_PAYMENT_METHODS.NEW_CARD,
        };
    } else {
        const manageCardDetails = getManageCardDetails();

        if (manageCardDetails != null) {
            secureCardData = {
                savedCardDetails: {
                    nickName: manageCardDetails.nickname,
                },
                paymentMethod: VALID_PAYMENT_METHODS.CARD,
            };
        }
    }

    return secureCardData;
};

export const getTransactionConfirmationRedirectUrl = (): string => {
    return `${location.protocol}//${location.host}/payment/transaction-received`;
};
